import { useCallback, useContext, useState } from "react";

import { inviteOrganisationToProject } from "../../../../../service/organisation";
import { ServiceError, Status } from "../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../service/ValidationErrorFormatter";
import { useAuth } from "../../../../../useAuth";
import { Toast } from "../../../../../widget";
import { ProjectContext } from "../../project/ProjectContext";
import {
  getProjectAccessRequestRole,
  GUEST_WRITE_ROLE,
  shouldRetryAsGuestWrite,
} from "./utils/requestProjectAccessModal.utils";

interface UseRequestProjectAccessModalReturnData {
  errors: ServiceError[] | undefined;
  isSendRequestLoading: boolean;
  onSendRequest: () => Promise<void>;
}

export const useRequestProjectAccessModal = (
  onClose: () => void,
  onRequestSent?: () => void
): UseRequestProjectAccessModalReturnData => {
  const { currentOrganisationUuid, currentUserType } = useAuth();
  const { publicProjectDetails } = useContext(ProjectContext);
  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [isSendRequestLoading, setIsSendRequestLoading] = useState(false);

  const onSendRequest = useCallback(async (): Promise<void> => {
    const projectUuid = publicProjectDetails?.uuid;

    if (!projectUuid) {
      setErrors([
        {
          code: "NO_PROJECT",
          message: "A project must be selected",
        },
      ]);
      return;
    }

    setIsSendRequestLoading(true);
    setErrors(undefined);

    const sendRequest = (role: string): ReturnType<typeof inviteOrganisationToProject> =>
      inviteOrganisationToProject({
        projectUuid,
        type: "Request",
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        targetOrganisationUuid: publicProjectDetails!.responsibleOwnerOrganisationUuid!,
        targetOrganisationEmailOverride: null,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        sourceOrganisationUuid: currentOrganisationUuid!,
        role,
        // A request to be let in, not a grant - the responsible owner decides, and cannot be made to
        // hand the role over, or to drop its developer, by the organisation asking for access.
        makeResponsibleOwner: false,
        existingDeveloperAction: null,
      });

    try {
      const role = getProjectAccessRequestRole(currentUserType, publicProjectDetails?.developer?.uuid);
      let inviteResponse = await sendRequest(role);

      // BR-02878 - the project already has a Developer invite awaiting a response, which the requester
      // cannot see beforehand, so a developer asks to join as a guest instead.
      if (shouldRetryAsGuestWrite(role, inviteResponse.errors)) {
        inviteResponse = await sendRequest(GUEST_WRITE_ROLE);
      }

      if (inviteResponse.status === Status.Success) {
        Toast.success({ message: "Access request sent" });
        onRequestSent?.();
        onClose();
        return;
      }

      setErrors(inviteResponse.errors);
      if (inviteResponse.errors?.length) {
        Toast.error({ message: getErrorMessageFromCode(inviteResponse.errors[0].code) });
      } else {
        Toast.error({ message: "Access request failed to send" });
      }
    } finally {
      setIsSendRequestLoading(false);
    }
  }, [currentOrganisationUuid, currentUserType, onClose, onRequestSent, publicProjectDetails]);

  return {
    errors,
    isSendRequestLoading,
    onSendRequest,
  };
};
